import { useCallback, memo } from "react";
import {
  Paper,
  Typography,
  Divider,
  Box,
  Button,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Devices,
  Settings,
  Add,
  CreditCard,
  AccountBalance,
  Fence,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BillingService } from "../../services/BillingService";
import { CONFIG } from "../../config";

const QuickActionsPanel = memo(({ canAddDevice, userDB, onAddDeviceClick }) => {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const navigate = useNavigate();

  const openBillingPortal = useCallback(() => {
    BillingService.getBillingPortal().then((data) => {
      window.location.href = data.url;
    });
  }, []);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(145deg, rgba(40,40,45,0.9) 0%, rgba(35,35,40,0.8) 100%)"
            : "linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(245,245,245,0.8) 100%)",
        backdropFilter: "blur(10px)",
        border: `1px solid ${
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.1)"
            : "rgba(0,0,0,0.1)"
        }`,
        height: "fit-content",
      }}
    >
      <Typography variant="h6" fontWeight={600} mb={3}>
        {t("common.quick_actions")}
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Box display="flex" flexDirection="column" gap={2}>
        {/* Add Device - only shown if user has permission */}
        {canAddDevice && (
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={onAddDeviceClick}
            fullWidth
            sx={{
              borderRadius: 2,
              justifyContent: "flex-start",
              textAlign: "left",
              py: 1.5,
              color: theme.palette.success.main,
              borderColor: theme.palette.success.main,
              "&:hover": {
                borderColor: theme.palette.success.main,
                backgroundColor: alpha(theme.palette.success.main, 0.1),
              },
            }}
          >
            {t("common.add_device")}
          </Button>
        )}

        <Button
          variant="outlined"
          startIcon={<Devices />}
          onClick={() => navigate("/device")}
          fullWidth
          sx={{
            borderRadius: 2,
            justifyContent: "flex-start",
            textAlign: "left",
            py: 1.5,
          }}
        >
          {t("common.view_all_devices")}
        </Button>

        <Button
          variant="outlined"
          startIcon={<Fence />}
          onClick={() => navigate("/geofence")}
          fullWidth
          sx={{
            borderRadius: 2,
            justifyContent: "flex-start",
            textAlign: "left",
            py: 1.5,
          }}
        >
          {t("common.manage_geofences")}
        </Button>

        <Button
          variant="outlined"
          startIcon={<Settings />}
          onClick={() => navigate("/account")}
          fullWidth
          sx={{
            borderRadius: 2,
            justifyContent: "flex-start",
            textAlign: "left",
            py: 1.5,
          }}
        >
          {t("common.account_settings")}
        </Button>

        {/* Billing Actions - only shown if billing is enabled */}
        {CONFIG.billing_enabled ? (
          <>
            {userDB?.stripe_billing_status === "active" && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<AccountBalance />}
                  onClick={openBillingPortal}
                  fullWidth
                  sx={{
                    borderRadius: 2,
                    justifyContent: "flex-start",
                    textAlign: "left",
                    py: 1.5,
                    color: "#00c9a7",
                    borderColor: "#00c9a7",
                    "&:hover": {
                      borderColor: "#00c9a7",
                      backgroundColor: alpha("#00c9a7", 0.05),
                    },
                  }}
                >
                  {t("common.billing_portal")}
                </Button>
              </>
            )}
          </>
        ) : (
          //register card
          <Button
            variant="outlined"
            startIcon={<CreditCard />}
            onClick={openBillingPortal}
            fullWidth
            sx={{
              borderRadius: 2,
              justifyContent: "flex-start",
              textAlign: "left",
              py: 1.5,
            }}
          >
            {t("common.register_card")}
          </Button>
        )}
      </Box>
    </Paper>
  );
});

QuickActionsPanel.displayName = "QuickActionsPanel";

export default QuickActionsPanel;
